import { useState } from 'react';
import { z } from 'zod';
import { CommonRegex, IFormSchema, mapTextField, useReactForm } from '@ninja/utilities';
import { GlobalAPIError, postCustomerContact } from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import { selectSetIsOfflineSubmitted, useQuoteStore } from '../../../stores';

interface OfflineFormValues {
	email?: string;
	mobile?: string;
}

type UseOfflineFormProps = {
	encryptedJourneyId: string;
};

export const useOfflineForm = ({ encryptedJourneyId }: UseOfflineFormProps) => {
	const { apiConnectionOptions, translate, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const setIsOfflineSubmitted = useQuoteStore(selectSetIsOfflineSubmitted);
	const [checkboxStatus, setCheckboxStatus] = useState({ isChecked: false, hasError: false });
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isDialogLoading, setIsDialogLoading] = useState(false);

	const schema: IFormSchema<OfflineFormValues> = {
		fields: [
			mapTextField({
				field: 'email',
				maxLength: 50,
				label: translate('offline:form.email.label'),
			}),

			mapTextField({
				field: 'mobile',
				label: translate('offline:form.mobile.label'),
				directionForInput: 'ltr',
				suffix: '+971',
				maxLength: 11,
				mask: '99 999 9999',
			}),
		],
		initialValues: {},
		validationMode: 'zod',
		validation: z.object({
			email: z.string().regex(CommonRegex.email, translate('offline:form.email.error')),
			mobile: z.string().regex(CommonRegex.uaeMobileNumber, translate('offline:form.mobile.error')),
		}),
	};

	const form = useReactForm<OfflineFormValues>({
		initialValues: schema.initialValues,
		validationMode: schema.validationMode,
		validation: schema.validation,
		validateBehavior: 'on-submit',
		onFormSubmit: async (values) => {
			if (!checkboxStatus.isChecked) {
				setCheckboxStatus({ isChecked: false, hasError: true });

				return;
			}

			try {
				await postCustomerContact(apiConnectionOptions)({
					body: {
						email: values.email!,
						mobileNumber: values.mobile!,
					},
					parameters: { encryptedJourneyId },
				});

				setIsOfflineSubmitted(true);
				setIsDialogOpen(true);
			} catch (error) {
				onGeneralErrorHandling(error as GlobalAPIError);
			}
		},
	});

	return {
		fieldsStructure: schema.fields,
		form,
		checkboxStatus,
		setCheckboxStatus,
		isDialogOpen,
		setIsDialogOpen,
		isDialogLoading,
		setIsDialogLoading,
	};
};
